<div class="store-config">
  <!-- Header Section -->
  <div class="row mb-4">
    <div class="col-md-6">
      <h4>Store Configuration</h4>
      <p class="text-muted">Manage your store settings and preferences</p>
    </div>
    <div class="col-md-6 d-flex justify-content-end align-items-center gap-2">
      <span *ngIf="hasUnsavedChanges" class="badge bg-warning text-dark">Unsaved Changes</span>
      <button 
        type="button" 
        class="btn btn-success px-4 py-2 fw-bold"
        [disabled]="saving"
        (click)="saveSettings()"
        style="min-width: 140px;">
        <nb-icon *ngIf="saving" icon="loader-outline" class="me-2"></nb-icon>
        <nb-icon *ngIf="!saving" icon="save-outline" class="me-2"></nb-icon>
        {{ saving ? 'Saving...' : 'Save Changes' }}
      </button>
    </div>
  </div>

  <!-- Tab Navigation -->
  <nb-tabset>
    <nb-tab tabTitle="Basic Settings">
      <div class="tab-content">
        <form [formGroup]="storeSettingsForm" *ngIf="!loading">
          
          <!-- Store Information -->
          <nb-card class="mb-4">
            <nb-card-header>
              <h6 class="mb-0">
                <nb-icon icon="home-outline" class="me-2"></nb-icon>
                Store Information
              </h6>
            </nb-card-header>
            <nb-card-body>
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Store Name *</label>
                    <input type="text" class="form-control" formControlName="storeName" placeholder="Enter store name">
                    <div *ngIf="storeSettingsForm.get('storeName')?.invalid && storeSettingsForm.get('storeName')?.touched" class="text-danger small mt-1">
                      Store name is required (min 2 characters)
                    </div>
                  </div>
                </div>
                
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Currency *</label>
                    <select class="form-select" formControlName="currency">
                      <option *ngFor="let currency of currencyOptions" [value]="currency.value">
                        {{ currency.label }}
                      </option>
                    </select>
                    <div class="form-text">
                      <nb-icon icon="info-outline" class="me-1"></nb-icon>
                      All products will use this currency. Changing this affects all existing products.
                    </div>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Commission Rate (%) *</label>
                    <div class="input-group">
                      <input type="number" class="form-control" formControlName="commissionRate" min="0" max="100" step="0.1">
                      <span class="input-group-text">%</span>
                    </div>
                    <div class="form-text">Platform commission rate for sales</div>
                  </div>
                </div>
                
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Payout Schedule</label>
                    <select class="form-select" formControlName="payoutSchedule">
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-12">
                  <div class="form-check mb-3">
                    <input type="checkbox" class="form-check-input" formControlName="isEcommerceEnabled" id="ecommerceEnabled">
                    <label class="form-check-label" for="ecommerceEnabled">
                      <strong>Enable Ecommerce</strong>
                      <br>
                      <small class="text-muted">Allow customers to purchase products from this store</small>
                    </label>
                  </div>
                  
                  <div class="form-check mb-3">
                    <input type="checkbox" class="form-check-input" formControlName="inventoryManagementEnabled" id="inventoryEnabled">
                    <label class="form-check-label" for="inventoryEnabled">
                      <strong>Enable Inventory Management</strong>
                      <br>
                      <small class="text-muted">Track stock levels and prevent overselling</small>
                    </label>
                  </div>
                  
                  <div *ngIf="storeSettingsForm.get('inventoryManagementEnabled')?.value" class="alert alert-warning mt-3 mb-3">
                    <nb-icon icon="alert-triangle-outline" class="me-2"></nb-icon>
                    <strong>Impact Notice:</strong> Enabling inventory management will affect cart validation for all customers. 
                    Products will be limited to available stock quantities.
                  </div>

                  <div class="form-check">
                    <input type="checkbox" class="form-check-input" formControlName="skuRequirementEnabled" id="skuRequired">
                    <label class="form-check-label" for="skuRequired">
                      <strong>Require SKU for Products</strong>
                      <br>
                      <small class="text-muted">Make SKU mandatory for all product creation and updates</small>
                    </label>
                  </div>
                  
                  <div *ngIf="storeSettingsForm.get('skuRequirementEnabled')?.value" class="alert alert-warning mt-3">
                    <nb-icon icon="alert-triangle-outline" class="me-2"></nb-icon>
                    <strong>Impact Notice:</strong> Existing products without SKU will need to be updated before new products can be created.
                    Product operations require valid SKU fields.
                  </div>

                  <div class="mb-3 mt-4">
                    <label class="form-label">Default category for catalog imports</label>
                    <select class="form-select" formControlName="catalogImportDefaultCategoryId">
                      <option value="">No default (choose manually)</option>
                      <option *ngFor="let category of storeCategories" [value]="category._id">
                        {{ category.name }}
                      </option>
                    </select>
                    <div class="form-text text-muted">
                      Pre-filled as Category when you add a product from the Global Product Catalog. You can still change the category on each product.
                    </div>
                    <div *ngIf="isSavedCatalogImportCategoryMissing()" class="text-warning small mt-1">
                      The saved default category no longer exists — choose another or clear it.
                    </div>
                  </div>

                  <div class="mb-3 mt-4 pt-3 border-top">
                    <label class="form-label">Global category mapping</label>
                    <p class="form-text text-muted mb-2">
                      Map each global catalog category to a store category. Used when adding products from the Global Product Catalog (after explicit mapping, then name match, then the default above).
                    </p>
                    <div *ngIf="globalCategoryMappingLoadError" class="alert alert-warning py-2 small">
                      {{ globalCategoryMappingLoadError }}
                    </div>
                    <div *ngIf="!globalCategoryMappingLoadError && globalCategoriesFlat.length === 0" class="text-muted small">
                      No global categories defined yet.
                    </div>
                    <div *ngIf="globalCategoriesFlat.length > 0" class="table-responsive mb-2">
                      <table class="table table-sm table-hover align-middle mb-0">
                        <thead class="table-light">
                          <tr>
                            <th>Global category</th>
                            <th style="min-width: 220px">Store category</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr *ngFor="let row of globalCategoryMappingRows">
                            <td>
                              <span [style.padding-left.px]="row.depth * 16">{{ row.path }}</span>
                            </td>
                            <td>
                              <select class="form-select form-select-sm"
                                [(ngModel)]="row.storeCategoryId"
                                [ngModelOptions]="{standalone: true}">
                                <option value="">— Not mapped —</option>
                                <option *ngFor="let sc of activeStoreCategoriesForMapping" [value]="sc._id">{{ sc.name }}</option>
                              </select>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="d-flex gap-2 flex-wrap" *ngIf="globalCategoriesFlat.length > 0">
                      <button type="button" class="btn btn-outline-secondary btn-sm"
                        (click)="matchGlobalCategoriesByName()"
                        [disabled]="globalCategoryMappingSaving || !storeCategoriesLoaded">
                        Match by name
                      </button>
                      <button type="button" class="btn btn-primary btn-sm"
                        (click)="saveGlobalCategoryMappings()"
                        [disabled]="globalCategoryMappingSaving">
                        {{ globalCategoryMappingSaving ? 'Saving…' : 'Save mappings' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </nb-card-body>
          </nb-card>

          <!-- Currency Impact Notice -->
          <nb-alert *ngIf="storeSettingsForm.get('currency')?.dirty" status="warning" class="mb-4">
            <nb-icon icon="alert-triangle-outline" class="me-2"></nb-icon>
            <strong>Currency Change Notice:</strong> Changing the store currency will affect all existing products. 
            All product prices will be displayed in the new currency without automatic conversion.
          </nb-alert>



        </form>

        <!-- Loading State -->
        <div *ngIf="loading" class="text-center py-5">
          <nb-spinner size="large"></nb-spinner>
          <p class="mt-3 text-muted">Loading store settings...</p>
        </div>
      </div>
    </nb-tab>

    <nb-tab tabTitle="Bank Details">
      <div class="tab-content">
        <form [formGroup]="storeSettingsForm" *ngIf="!loading">
          
          <nb-card class="mb-4">
            <nb-card-header>
              <h6 class="mb-0">
                <nb-icon icon="credit-card-outline" class="me-2"></nb-icon>
                Banking Information
              </h6>
            </nb-card-header>
            <nb-card-body>
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Account Number</label>
                    <input type="text" class="form-control" formControlName="bankAccountNumber" placeholder="Enter bank account number">
                  </div>
                </div>
                
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Bank Name</label>
                    <input type="text" class="form-control" formControlName="bankName" placeholder="Enter bank name">
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">SWIFT Code</label>
                    <input type="text" class="form-control" formControlName="swiftCode" placeholder="Enter SWIFT/BIC code">
                    <div class="form-text">International bank identifier (e.g., DABADKKK)</div>
                  </div>
                </div>
                
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Account Holder Name</label>
                    <input type="text" class="form-control" formControlName="bankAccountHolderName" placeholder="Enter account holder name">
                  </div>
                </div>
              </div>

              <nb-alert status="info">
                <nb-icon icon="info-outline" class="me-2"></nb-icon>
                Bank details are used for payout processing. Ensure all information is accurate.
              </nb-alert>
            </nb-card-body>
          </nb-card>



        </form>
      </div>
    </nb-tab>

    <nb-tab tabTitle="Tax Information">
      <div class="tab-content">
        <form [formGroup]="storeSettingsForm" *ngIf="!loading">
          
          <nb-card class="mb-4">
            <nb-card-header>
              <h6 class="mb-0">
                <nb-icon icon="file-text-outline" class="me-2"></nb-icon>
                Tax Configuration
              </h6>
            </nb-card-header>
            <nb-card-body>
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">VAT Number</label>
                    <input type="text" class="form-control" formControlName="vatNumber" placeholder="Enter VAT number">
                    <div class="form-text">Value Added Tax registration number</div>
                  </div>
                </div>
                
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Tax ID</label>
                    <input type="text" class="form-control" formControlName="taxId" placeholder="Enter tax ID">
                    <div class="form-text">Business tax identification number</div>
                  </div>
                </div>
              </div>
              
              <div class="row">
                <div class="col-md-12">
                  <div class="mb-3">
                    <label class="form-label">Business Registration</label>
                    <input type="text" class="form-control" formControlName="businessRegistration" placeholder="Enter business registration number">
                    <div class="form-text">Official business registration number</div>
                  </div>
                </div>
              </div>

              <nb-alert status="info">
                <nb-icon icon="info-outline" class="me-2"></nb-icon>
                Tax information is used for financial reporting and compliance purposes.
              </nb-alert>
            </nb-card-body>
          </nb-card>



        </form>
      </div>
    </nb-tab>

    <nb-tab tabTitle="Deposit Settings">
      <div class="tab-content">
        <form [formGroup]="storeSettingsForm" *ngIf="!loading">
          
          <!-- Deposit System Configuration -->
          <nb-card class="mb-4">
            <nb-card-header>
              <h6 class="mb-0">
                <nb-icon icon="archive-outline" class="me-2"></nb-icon>
                Deposit System Configuration
              </h6>
            </nb-card-header>
            <nb-card-body>
              <div class="alert alert-info mb-4">
                <nb-icon icon="info-outline" class="me-2"></nb-icon>
                <strong>Deposit System:</strong> Configure recyclable deposit fees for bottles, cans, and containers. Customers pay the deposit at checkout and receive it back when returning the container for recycling.
              </div>

              <!-- Enable/Disable Deposit System -->
              <div class="row mb-4">
                <div class="col-md-12">
                  <div class="form-check">
                    <input type="checkbox" class="form-check-input" formControlName="depositEnabled" id="depositEnabled">
                    <label class="form-check-label" for="depositEnabled">
                      <strong>🍾 Enable Deposit System</strong>
                      <br>
                      <small class="text-muted">Allow products to have recyclable deposit fees (bottles, cans, containers)</small>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Auto-assign by EAN toggle -->
              <div *ngIf="storeSettingsForm.get('depositEnabled')?.value" class="row mb-3">
                <div class="col-md-12">
                  <div class="form-check">
                    <input type="checkbox" class="form-check-input" formControlName="depositAutoAssignByEAN" id="depositAutoAssignByEAN">
                    <label class="form-check-label" for="depositAutoAssignByEAN">
                      <strong>Auto-assign deposit type by EAN</strong>
                      <br>
                      <small class="text-muted">When enabled, products with EANs in a known range are automatically assigned the matching deposit type on import or creation.</small>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Dynamic fee types table -->
              <div *ngIf="storeSettingsForm.get('depositEnabled')?.value" class="deposit-fee-types-table">
                <h6 class="mb-2">Fee Type Overrides</h6>
                <p class="text-muted small mb-3">
                  Amounts are pre-filled with global defaults. Override per-store as needed.
                  Manage the global catalog under
                  <a routerLink="/deposit-fee-catalog" class="text-decoration-none">Deposit Fee Catalog</a>.
                </p>

                <div *ngIf="depositFeeTypes.length === 0" class="alert alert-warning">
                  <nb-icon icon="alert-triangle-outline" class="me-2"></nb-icon>
                  No active deposit fee types found in the global catalog. Go to
                  <a routerLink="/deposit-fee-catalog">Deposit Fee Catalog</a> to create them first.
                </div>

                <div *ngIf="depositFeeTypes.length > 0" class="table-responsive">
                  <table class="table table-hover align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Fee Type</th>
                        <th>Description</th>
                        <th>EAN Range</th>
                        <th>Provider code</th>
                        <th style="width:160px">Amount ({{ getCurrencySymbol(storeSettingsForm.get('currency')?.value) }})</th>
                        <th class="text-center" style="width:90px">Enabled</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let entry of storeFeeTypeEntries; let i = index" [class.table-secondary]="!entry.enabled">
                        <td><strong>{{ entry.name }}</strong></td>
                        <td><span class="text-muted small">{{ entry.description || '—' }}</span></td>
                        <td>
                          <code class="small" *ngIf="entry.eanRangeStart || entry.eanRangeEnd">
                            {{ entry.eanRangeStart || '?' }} – {{ entry.eanRangeEnd || '?' }}
                          </code>
                          <span *ngIf="!entry.eanRangeStart && !entry.eanRangeEnd" class="text-muted small">—</span>
                        </td>
                        <td>
                          <code class="small" *ngIf="entry.providerCode">{{ entry.providerCode }}</code>
                          <span *ngIf="!entry.providerCode" class="text-muted small">—</span>
                        </td>
                        <td>
                          <div class="input-group input-group-sm">
                            <input
                              type="number"
                              class="form-control form-control-sm"
                              [(ngModel)]="entry.amount"
                              [ngModelOptions]="{standalone: true}"
                              step="0.01"
                              min="0"
                              [placeholder]="entry.defaultAmount">
                          </div>
                          <small class="text-muted" *ngIf="entry.amount !== entry.defaultAmount">
                            Default: {{ entry.defaultAmount | number:'1.2-2' }}
                          </small>
                        </td>
                        <td class="text-center">
                          <div class="form-check form-switch d-flex justify-content-center">
                            <input
                              class="form-check-input"
                              type="checkbox"
                              [(ngModel)]="entry.enabled"
                              [ngModelOptions]="{standalone: true}"
                              [id]="'ft-enabled-' + i">
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Disabled State Information -->
              <div *ngIf="!storeSettingsForm.get('depositEnabled')?.value" class="alert alert-warning">
                <nb-icon icon="alert-triangle-outline" class="me-2"></nb-icon>
                <strong>Deposit System Disabled:</strong> Enable the deposit system above to configure recyclable container deposits. This feature helps comply with local recycling regulations and environmental requirements.
              </div>
              
            </nb-card-body>
          </nb-card>

        </form>
        
        <!-- Loading State -->
        <div *ngIf="loading" class="text-center p-5">
          <ngx-spinner bdColor="rgba(0,0,0,0.1)" size="medium" color="#fff" type="ball-scale-multiple"></ngx-spinner>
          <p class="mt-3 text-muted">Loading deposit settings...</p>
        </div>
      </div>
    </nb-tab>

    <!-- TOMRA Vouchers Tab -->
    <nb-tab tabTitle="TOMRA Vouchers" tabIcon="shopping-bag-outline">
      <div class="tab-content">
        <form [formGroup]="storeSettingsForm" *ngIf="!loading">
          <nb-card class="mb-4">
            <nb-card-header>
              <h6 class="mb-0">
                <nb-icon icon="shopping-bag-outline" class="me-2"></nb-icon>
                TOMRA Voucher Configuration
              </h6>
            </nb-card-header>
            <nb-card-body>
              <p class="text-muted mb-4">
                Configure TOMRA reverse vending machine deposit vouchers for this store. When enabled, the checkout will accept EAN-13 TOMRA deposit vouchers as payment.
              </p>

              <div formGroupName="tomraConfig">

                <!-- Enable toggle -->
                <div class="row mb-3">
                  <div class="col-sm-12">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" formControlName="enabled" id="tomraEnabled">
                      <label class="form-check-label" for="tomraEnabled">
                        <strong>Enable TOMRA deposit voucher scanning</strong>
                      </label>
                    </div>
                  </div>
                </div>

                <div *ngIf="storeSettingsForm.get('tomraConfig.enabled')?.value">

                  <!-- Mode selector -->
                  <div class="form-group mt-3 mb-3">
                    <label class="d-block font-weight-bold mb-2"><strong>Mode</strong></label>
                    <div class="form-check form-check-inline">
                      <input class="form-check-input" type="radio" id="tomraModeOffline"
                             name="tomraMode" value="offline"
                             formControlName="mode">
                      <label class="form-check-label" for="tomraModeOffline">
                        Offline (PLU-based)
                      </label>
                    </div>
                    <div class="form-check form-check-inline">
                      <input class="form-check-input" type="radio" id="tomraModeApi"
                             name="tomraMode" value="api"
                             formControlName="mode">
                      <label class="form-check-label" for="tomraModeApi">
                        API (TOMRA Voucher Control)
                      </label>
                    </div>
                  </div>

                  <!-- API credentials section — shown only in API mode -->
                  <div class="card mt-3 mb-4" *ngIf="storeSettingsForm.get('tomraConfig.mode')?.value === 'api'">
                    <div class="card-body">
                      <h6 class="card-title">TOMRA Voucher Control API Credentials</h6>

                      <div class="form-group mb-3">
                        <label class="form-label">API Base URL</label>
                        <input type="text" class="form-control"
                               placeholder="https://api.eu.prod.tomra.cloud"
                               formControlName="apiBaseUrl">
                      </div>

                      <div class="form-group mb-3">
                        <label class="form-label">Realm</label>
                        <input type="text" class="form-control"
                               placeholder="Obtained from TOMRA Developer Portal"
                               formControlName="realm">
                      </div>

                      <div class="form-group mb-3">
                        <label class="form-label">Client ID</label>
                        <input type="text" class="form-control"
                               formControlName="clientId">
                      </div>

                      <div class="form-group mb-3">
                        <label class="form-label">Client Secret</label>
                        <input type="password" class="form-control"
                               formControlName="clientSecret"
                               placeholder="Leave blank to keep existing secret">
                      </div>

                      <div class="d-flex align-items-center mt-2">
                        <button class="btn btn-outline-primary btn-sm mr-2"
                                type="button"
                                (click)="testTomraConnection()"
                                [disabled]="tomraConnectionLoading">
                          <span *ngIf="tomraConnectionLoading" class="spinner-border spinner-border-sm mr-1"></span>
                          Test Connection
                        </button>
                        <span *ngIf="tomraConnectionStatus === 'success'" class="text-success ms-2">
                          &#10003; Connected
                        </span>
                        <span *ngIf="tomraConnectionStatus && tomraConnectionStatus.startsWith('error:')" class="text-danger ms-2">
                          &#10007; Error: {{ tomraConnectionStatus.replace('error:', '') }}
                        </span>
                      </div>

                      <small class="text-muted d-block mt-2">
                        Credentials are issued via the TOMRA Developer Portal (developer.tomra.com).
                        API mode validates vouchers in real time and prevents double-redemption.
                      </small>
                    </div>
                  </div>

                  <!-- Info note -->
                  <div class="alert alert-info mb-4">
                    <nb-icon icon="info-outline" class="me-2"></nb-icon>
                    Enter the PLU from the first 7 digits of a printed TOMRA deposit voucher barcode (e.g. barcode <code>2339900085007</code> → PLU <code>2339900</code>).
                  </div>

                  <!-- Offline fallback label shown in API mode -->
                  <small class="text-muted d-block mt-3 mb-2"
                         *ngIf="storeSettingsForm.get('tomraConfig.mode')?.value === 'api'">
                    Offline fallback PLUs (used if TOMRA API is unreachable)
                  </small>

                  <!-- Vouchers table -->
                  <div *ngIf="tomraVouchers.length > 0" class="table-responsive mb-3">
                    <table class="table table-hover align-middle">
                      <thead class="table-light">
                        <tr>
                          <th>PLU (first 7 digits of barcode)</th>
                          <th>Display name on receipt</th>
                          <th class="text-center" style="width: 80px;">Remove</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr *ngFor="let voucher of tomraVouchers; let i = index">
                          <td>
                            <input
                              type="text"
                              class="form-control form-control-sm"
                              [(ngModel)]="voucher.plu"
                              [ngModelOptions]="{standalone: true}"
                              maxlength="7"
                              placeholder="2339900">
                          </td>
                          <td>
                            <input
                              type="text"
                              class="form-control form-control-sm"
                              [(ngModel)]="voucher.name"
                              [ngModelOptions]="{standalone: true}"
                              placeholder="TOMRA Flaskebon">
                          </td>
                          <td class="text-center">
                            <button
                              type="button"
                              class="btn btn-outline-danger btn-sm"
                              (click)="removeTomraVoucher(i)"
                              title="Remove this voucher">
                              <nb-icon icon="trash-2-outline"></nb-icon>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div *ngIf="tomraVouchers.length === 0" class="alert alert-warning mb-3">
                    <nb-icon icon="alert-triangle-outline" class="me-2"></nb-icon>
                    No TOMRA machines registered yet. Click "Add TOMRA machine" to register one.
                  </div>

                  <!-- Add machine button -->
                  <div class="mb-4">
                    <button type="button" class="btn btn-outline-primary btn-sm" (click)="addTomraVoucher()">
                      <nb-icon icon="plus-outline" class="me-1"></nb-icon>
                      Add TOMRA machine
                    </button>
                  </div>

                  <!-- Save button -->
                  <div class="mt-2">
                    <button class="btn btn-primary btn-sm" type="button"
                      [disabled]="tomraSaving"
                      (click)="saveTomraSettings()">
                      <nb-icon icon="save-outline" class="me-1"></nb-icon>
                      {{ tomraSaving ? 'Saving...' : 'Save TOMRA Settings' }}
                    </button>
                  </div>

                </div>

                <!-- Disabled state -->
                <div *ngIf="!storeSettingsForm.get('tomraConfig.enabled')?.value" class="alert alert-warning mt-3">
                  <nb-icon icon="alert-triangle-outline" class="me-2"></nb-icon>
                  <strong>TOMRA Integration Disabled:</strong> Enable the toggle above to configure TOMRA deposit voucher scanning for this store.
                </div>

              </div>
            </nb-card-body>
          </nb-card>
        </form>

        <!-- Loading State -->
        <div *ngIf="loading" class="text-center p-5">
          <nb-spinner size="large"></nb-spinner>
          <p class="mt-3 text-muted">Loading settings...</p>
        </div>
      </div>
    </nb-tab>

    <nb-tab tabTitle="Summary">
      <div class="tab-content">
        <nb-card *ngIf="!loading">
          <nb-card-header>
            <h6 class="mb-0">
              <nb-icon icon="eye-outline" class="me-2"></nb-icon>
              Settings Overview
            </h6>
          </nb-card-header>
          <nb-card-body>
            
            <!-- Store Overview -->
            <div class="settings-summary">
              <div class="row mb-3">
                <div class="col-md-3">
                  <strong>Store Name:</strong>
                </div>
                <div class="col-md-9">
                  {{ storeSettingsForm.get('storeName')?.value || 'Not set' }}
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-3">
                  <strong>Currency:</strong>
                </div>
                <div class="col-md-9">
                  <span class="badge badge-info">
                    {{ getCurrencySymbol(storeSettingsForm.get('currency')?.value) }} 
                    {{ storeSettingsForm.get('currency')?.value }}
                  </span>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-3">
                  <strong>Commission Rate:</strong>
                </div>
                <div class="col-md-9">
                  {{ storeSettingsForm.get('commissionRate')?.value }}%
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-3">
                  <strong>Payout Schedule:</strong>
                </div>
                <div class="col-md-9">
                  {{ storeSettingsForm.get('payoutSchedule')?.value | titlecase }}
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-3">
                  <strong>Ecommerce Status:</strong>
                </div>
                <div class="col-md-9">
                  <span [class]="storeSettingsForm.get('isEcommerceEnabled')?.value ? 'badge badge-success' : 'badge badge-warning'">
                    {{ storeSettingsForm.get('isEcommerceEnabled')?.value ? 'Enabled' : 'Disabled' }}
                  </span>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-3">
                  <strong>Inventory Management:</strong>
                </div>
                <div class="col-md-9">
                  <span [class]="storeSettingsForm.get('inventoryManagementEnabled')?.value ? 'badge badge-success' : 'badge badge-secondary'">
                    {{ storeSettingsForm.get('inventoryManagementEnabled')?.value ? 'Enabled' : 'Disabled' }}
                  </span>
                  <small *ngIf="storeSettingsForm.get('inventoryManagementEnabled')?.value" class="text-muted d-block mt-1">
                    Stock tracking and validation active
                  </small>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-3">
                  <strong>SKU Requirement:</strong>
                </div>
                <div class="col-md-9">
                  <span [class]="storeSettingsForm.get('skuRequirementEnabled')?.value ? 'badge badge-success' : 'badge badge-secondary'">
                    {{ storeSettingsForm.get('skuRequirementEnabled')?.value ? 'Required' : 'Optional' }}
                  </span>
                  <small *ngIf="storeSettingsForm.get('skuRequirementEnabled')?.value" class="text-muted d-block mt-1">
                    SKU mandatory for all product operations
                  </small>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-3">
                  <strong>Catalog import category:</strong>
                </div>
                <div class="col-md-9">
                  {{ getCatalogImportDefaultCategorySummaryName() }}
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-3">
                  <strong>Deposit System:</strong>
                </div>
                <div class="col-md-9">
                  <span [class]="storeSettingsForm.get('depositEnabled')?.value ? 'badge badge-success' : 'badge badge-secondary'">
                    {{ storeSettingsForm.get('depositEnabled')?.value ? 'Enabled' : 'Disabled' }}
                  </span>
                  <div *ngIf="storeSettingsForm.get('depositEnabled')?.value" class="mt-2">
                    <small class="text-muted">
                      <strong>{{ storeFeeTypeEntries.length }}</strong> fee type(s) configured |
                      <strong>Auto EAN:</strong> {{ storeSettingsForm.get('depositAutoAssignByEAN')?.value ? 'On' : 'Off' }}
                    </small>
                  </div>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-3">
                  <strong>Bank Account:</strong>
                </div>
                <div class="col-md-9">
                  {{ storeSettingsForm.get('bankAccountNumber')?.value || 'Not configured' }}
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-3">
                  <strong>VAT Number:</strong>
                </div>
                <div class="col-md-9">
                  {{ storeSettingsForm.get('vatNumber')?.value || 'Not set' }}
                </div>
              </div>
            </div>

            <hr>

            <div class="text-center">
              <button 
                type="button" 
                class="btn btn-outline-secondary me-2"
                [disabled]="!hasUnsavedChanges"
                (click)="resetForm()">
                <nb-icon icon="refresh-outline" class="me-1"></nb-icon>
                Reset Changes
              </button>
              
              <button 
                type="button" 
                class="btn btn-success"
                [disabled]="saving || storeSettingsForm.invalid"
                (click)="saveSettings()">
                <nb-icon *ngIf="saving" icon="loader-outline" class="me-1"></nb-icon>
                <nb-icon *ngIf="!saving" icon="save-outline" class="me-1"></nb-icon>
                {{ saving ? 'Saving...' : 'Save All Settings' }}
              </button>
            </div>

          </nb-card-body>
        </nb-card>
      </div>
    </nb-tab>

    <!-- Breece Integration Tab -->
    <nb-tab tabTitle="Breece Integration" tabIcon="monitor-outline">
      <div class="tab-content">
        <form [formGroup]="storeSettingsForm" *ngIf="!loading">
          <nb-card class="mb-4">
            <nb-card-header>
              <h6 class="mb-0">
                <nb-icon icon="monitor-outline" class="me-2"></nb-icon>
                Breece ESL Integration
              </h6>
            </nb-card-header>
            <nb-card-body>
              <p class="text-muted mb-4">
                Connect this store to Breece electronic shelf labels. Each store requires its own API credentials from Breece.
              </p>

              <div formGroupName="breeceConfig">

                <!-- Enable toggle -->
                <div class="row mb-3">
                  <div class="col-sm-12">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" formControlName="enabled" id="breeceEnabled">
                      <label class="form-check-label" for="breeceEnabled">
                        <strong>Enable Breece Integration</strong>
                      </label>
                    </div>
                  </div>
                </div>

                <div *ngIf="storeSettingsForm.get('breeceConfig.enabled')?.value">
                  <!-- Client ID -->
                  <div class="row mb-3">
                    <div class="col-sm-6">
                      <label class="form-label">Client ID <span class="text-danger">*</span></label>
                      <input nbInput fullWidth formControlName="clientId" placeholder="Breece Client ID" />
                    </div>
                  </div>

                  <!-- Client Secret -->
                  <div class="row mb-3">
                    <div class="col-sm-6">
                      <label class="form-label">Client Secret <span class="text-danger">*</span></label>
                      <div class="input-group">
                        <input nbInput fullWidth
                          [type]="showBreeceSecret ? 'text' : 'password'"
                          formControlName="clientSecret"
                          placeholder="Breece Client Secret" />
                        <button class="btn btn-outline-secondary" type="button" (click)="showBreeceSecret = !showBreeceSecret">
                          <nb-icon [icon]="showBreeceSecret ? 'eye-off-outline' : 'eye-outline'"></nb-icon>
                        </button>
                      </div>
                      <small class="text-muted">Leave blank to keep existing secret.</small>
                    </div>
                  </div>

                  <!-- Installation ID -->
                  <div class="row mb-3">
                    <div class="col-sm-6">
                      <label class="form-label">Installation ID <span class="text-danger">*</span></label>
                      <input nbInput fullWidth type="number" formControlName="installationId" placeholder="e.g. 2257" />
                      <small class="text-muted mt-1 d-block">Enter the Breece installation ID this store maps to.</small>
                      <div *ngIf="breeceInstallations.length > 0" class="mt-2">
                        <select nbInput fullWidth class="form-select"
                          [ngModel]="storeSettingsForm.get('breeceConfig.installationId')?.value"
                          (ngModelChange)="storeSettingsForm.get('breeceConfig.installationId')?.setValue($event)"
                          [ngModelOptions]="{standalone: true}">
                          <option [ngValue]="null" disabled>Select an installation...</option>
                          <option *ngFor="let inst of breeceInstallations" [ngValue]="inst.id">
                            {{ inst.name }} (ID: {{ inst.id }})
                          </option>
                        </select>
                        <small class="text-muted mt-1 d-block">Run Test Connection to look up available installations for this Breece account.</small>
                      </div>
                    </div>
                  </div>

                  <!-- Test connection button -->
                  <div class="row mb-3">
                    <div class="col-sm-12">
                      <button nbButton status="info" type="button"
                        [disabled]="testingBreece"
                        (click)="testBreeceConnection()">
                        <nb-icon icon="wifi-outline"></nb-icon>
                        {{ testingBreece ? 'Testing...' : 'Test Connection' }}
                      </button>
                      <span *ngIf="breeceTestResult" class="ms-3"
                        [class.text-success]="breeceTestResult.success"
                        [class.text-danger]="!breeceTestResult.success">
                        {{ breeceTestResult.message }}
                      </span>
                    </div>
                  </div>

                  <!-- Sync Interval -->
                  <div class="row mb-3">
                    <div class="col-sm-6">
                      <label class="form-label">Auto-Sync Interval</label>
                      <div class="input-group input-group-sm">
                        <select class="form-select form-select-sm" formControlName="syncIntervalMinutes">
                          <option [ngValue]="10">Every 10 minutes</option>
                          <option [ngValue]="30">Every 30 minutes</option>
                          <option [ngValue]="60">Every 60 minutes (1 hour)</option>
                          <option [ngValue]="720">Every 720 minutes (12 hours)</option>
                          <option [ngValue]="1440">Every 1440 minutes (24 hours)</option>
                        </select>
                        <button class="btn btn-outline-secondary btn-sm" type="button"
                          [disabled]="breeceIntervalSaving"
                          (click)="saveBreeceInterval()">
                          <nb-icon icon="save-outline"></nb-icon>
                          {{ breeceIntervalSaving ? 'Saving...' : 'Save' }}
                        </button>
                      </div>
                      <small class="text-muted">How often the backend automatically syncs ESL prices to Breece.</small>
                    </div>
                  </div>

                  <!-- Last Successful Sync -->
                  <div class="row mb-3">
                    <div class="col-sm-6">
                      <label class="form-label">Last Successful Sync</label>
                      <div class="form-control-plaintext">
                        <span *ngIf="lastSuccessfulSyncAt; else neverSynced" class="text-success">
                          <nb-icon icon="checkmark-circle-2-outline" class="me-1"></nb-icon>
                          {{ lastSuccessfulSyncAt | date:'medium' }}
                        </span>
                        <ng-template #neverSynced>
                          <span class="text-muted fst-italic">Never</span>
                        </ng-template>
                      </div>
                    </div>
                  </div>

                  <!-- Manual Batch Sync -->
                  <div class="row mb-3">
                    <div class="col-sm-12">
                      <button class="btn btn-outline-secondary btn-sm" type="button"
                        [disabled]="breeceManualSyncing"
                        (click)="triggerManualBatchSync()">
                        <nb-icon icon="sync-outline"></nb-icon>
                        {{ breeceManualSyncing ? 'Starting sync...' : 'Manual Batch Sync Now' }}
                      </button>
                      <small class="text-muted ms-2">Trigger an immediate batch sync of all products to Breece.</small>
                    </div>
                  </div>

                  <!-- Save Breece Settings button -->
                  <div class="mt-3">
                    <button class="btn btn-primary btn-sm" type="button"
                      [disabled]="breeceSaving"
                      (click)="saveBreeceSettings()">
                      <nb-icon icon="save-outline"></nb-icon>
                      {{ breeceSaving ? 'Saving...' : 'Save Breece Settings' }}
                    </button>
                  </div>
                </div>

              </div>
            </nb-card-body>
          </nb-card>
        </form>
      </div>
    </nb-tab>

    <!-- Auto Z-Report Tab -->
    <nb-tab tabTitle="Auto Z-Report" tabIcon="clock-outline">
      <div class="tab-content">
        <form *ngIf="!loading">

          <!-- Settings Card -->
          <nb-card class="mb-4">
            <nb-card-header>
              <h6 class="mb-0">
                <nb-icon icon="clock-outline" class="me-2"></nb-icon>
                Automatic Z-Report Settings
              </h6>
            </nb-card-header>
            <nb-card-body>
              <p class="text-muted mb-4">
                Configure automatic Z-Report generation. When enabled, the backend will close the current period and generate a Z-Report at the scheduled time each day (in the store's local timezone).
              </p>

              <!-- Enable toggle -->
              <div class="row mb-3">
                <div class="col-sm-12">
                  <div class="form-check form-switch">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="autoZReportEnabled"
                      [(ngModel)]="autoZReportEnabled"
                      [ngModelOptions]="{standalone: true}">
                    <label class="form-check-label" for="autoZReportEnabled">
                      <strong>Enable automatic Z-Report generation</strong>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Scheduled time -->
              <div class="row mb-4" *ngIf="autoZReportEnabled">
                <div class="col-sm-4">
                  <label class="form-label">Scheduled time (store local time)</label>
                  <input
                    type="time"
                    class="form-control"
                    [(ngModel)]="autoZReportScheduledTime"
                    [ngModelOptions]="{standalone: true}"
                    [disabled]="!autoZReportEnabled">
                  <small class="text-muted">24-hour format — interpreted in the store's local timezone.</small>
                </div>
              </div>

              <!-- Save button -->
              <div class="mt-2">
                <button class="btn btn-primary btn-sm" type="button"
                  [disabled]="autoZReportSaving"
                  (click)="saveAutoZReportSettings()">
                  <nb-icon icon="save-outline"></nb-icon>
                  {{ autoZReportSaving ? 'Saving...' : 'Save Auto Z-Report Settings' }}
                </button>
              </div>
            </nb-card-body>
          </nb-card>

          <!-- Last Run Status Card -->
          <nb-card class="mb-4" *ngIf="autoZReportLastRunAt">
            <nb-card-header>
              <h6 class="mb-0">
                <nb-icon icon="activity-outline" class="me-2"></nb-icon>
                Last Execution
              </h6>
            </nb-card-header>
            <nb-card-body>
              <div class="row mb-3">
                <div class="col-md-4">
                  <label class="form-label text-muted small">Date / Time</label>
                  <div class="fw-bold">{{ autoZReportLastRunAt | date:'dd/MM/yyyy HH:mm' }}</div>
                </div>
                <div class="col-md-4">
                  <label class="form-label text-muted small">Status</label>
                  <div>
                    <span *ngIf="autoZReportLastRunStatus === 'success'" class="badge bg-success">Success</span>
                    <span *ngIf="autoZReportLastRunStatus === 'failed'" class="badge bg-danger">Failed</span>
                    <span *ngIf="autoZReportLastRunStatus !== 'success' && autoZReportLastRunStatus !== 'failed'" class="badge bg-secondary">{{ autoZReportLastRunStatus || 'Unknown' }}</span>
                  </div>
                </div>
              </div>
              <div *ngIf="autoZReportLastRunStatus === 'failed' && autoZReportLastRunError" class="alert alert-danger mb-0">
                <nb-icon icon="alert-circle-outline" class="me-2"></nb-icon>
                <strong>Error:</strong> {{ autoZReportLastRunError }}
              </div>
            </nb-card-body>
          </nb-card>

          <!-- Execution History Card -->
          <nb-card>
            <nb-card-header>
              <h6 class="mb-0">
                <nb-icon icon="list-outline" class="me-2"></nb-icon>
                Execution History
              </h6>
            </nb-card-header>
            <nb-card-body class="p-0">
              <!-- Empty state -->
              <div *ngIf="autoZReportHistory.length === 0" class="text-center p-4">
                <nb-icon icon="clock-outline" style="font-size: 2rem;" class="text-muted mb-2"></nb-icon>
                <p class="text-muted mb-0">No automatic executions yet.</p>
              </div>

              <!-- History table -->
              <div *ngIf="autoZReportHistory.length > 0" class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>Date / Time</th>
                      <th class="text-center">Status</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let entry of autoZReportHistory">
                      <td class="text-nowrap">{{ entry.executedAt | date:'dd/MM/yyyy HH:mm' }}</td>
                      <td class="text-center">
                        <span *ngIf="entry.status === 'success'" class="badge bg-success">Success</span>
                        <span *ngIf="entry.status === 'failed'" class="badge bg-danger">Failed</span>
                        <span *ngIf="entry.status !== 'success' && entry.status !== 'failed'" class="badge bg-secondary">{{ entry.status }}</span>
                      </td>
                      <td>
                        <span *ngIf="entry.error" class="text-danger small">{{ entry.error }}</span>
                        <span *ngIf="!entry.error" class="text-muted">—</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </nb-card-body>
          </nb-card>

        </form>

        <!-- Loading State -->
        <div *ngIf="loading" class="text-center p-5">
          <nb-spinner size="large"></nb-spinner>
          <p class="mt-3 text-muted">Loading settings...</p>
        </div>
      </div>
    </nb-tab>

    <nb-tab tabTitle="Products" tabIcon="cube-outline">
      <app-store-config-products-tab [storeId]="storeId"></app-store-config-products-tab>
    </nb-tab>

  </nb-tabset>
</div>